import React, { useState, useEffect } from 'react';
import { doc, setDoc, updateDoc, serverTimestamp, collection, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from '../../contexts/LocationContext';
import {
  MapPin, Phone, Clock, Loader, Check, Store, MessageCircle,
  Tag, User,
  ShoppingCart, Beef, Pill, Coffee, UtensilsCrossed,
  Scissors, Shirt, Smartphone, BookOpen, Leaf,Gem
} from 'lucide-react';
import Tooltip from '../common/Tooltip';

const COMMERCE_TYPES = [
  { key: 'boulangerie',    label: 'Boulangerie',     icon: Store           },
  { key: 'epicerie',       label: 'Épicerie',         icon: ShoppingCart    },
  { key: 'boucherie',      label: 'Boucherie',        icon: Beef            },
  { key: 'pharmacie',      label: 'Pharmacie',        icon: Pill            },
  { key: 'cafe',           label: 'Café / Snack',     icon: Coffee          },
  { key: 'restaurant',     label: 'Restaurant',       icon: UtensilsCrossed },
  { key: 'coiffeur',       label: 'Coiffeur',         icon: Scissors        },
  { key: 'vetements',      label: 'Vêtements',        icon: Shirt           },
  { key: 'electronique',   label: 'Électronique',     icon: Smartphone      },
  { key: 'librairie',      label: 'Librairie',        icon: BookOpen        },
  { key: 'fruits_legumes', label: 'Fruits & Légumes', icon: Leaf            },
  { key: 'bijouterie',     label: 'Bijouterie',       icon: Gem             }, 
  { key: 'autre',          label: 'Autre',            icon: Store           },
];

// ── Horaires ───────────────────────────────────────────────────────────────────
// DAYS_PARSE  : ordre canonique Lun→Dim utilisé par scheduleToString/parseScheduleString
//               Ne JAMAIS réordonner — les ranges Firestore en dépendent.
// DAYS_DISPLAY: ordre d'affichage dans l'UI (Sam en premier, Ven en dernier)
const DAYS_PARSE = [
  { key: 'lun', label: 'Lun' },
  { key: 'mar', label: 'Mar' },
  { key: 'mer', label: 'Mer' },
  { key: 'jeu', label: 'Jeu' },
  { key: 'ven', label: 'Ven' },
  { key: 'sam', label: 'Sam' },
  { key: 'dim', label: 'Dim' },
];

const DAYS_DISPLAY = [
  { key: 'sam', label: 'Sam' },
  { key: 'dim', label: 'Dim' },
  { key: 'lun', label: 'Lun' },
  { key: 'mar', label: 'Mar' },
  { key: 'mer', label: 'Mer' },
  { key: 'jeu', label: 'Jeu' },
  { key: 'ven', label: 'Ven' },
];

const SLOTS = [
  '6h','7h','7h30','8h','8h30','9h','10h','11h','12h',
  '13h','14h','15h','16h','17h','18h','19h','20h','21h','22h',
];

const DEFAULT_SCHEDULE = {
  sam: { open: true,  start: '8h', end: '18h' },
  dim: { open: true,  start: '8h', end: '18h' },
  lun: { open: true,  start: '8h', end: '18h' },
  mar: { open: true,  start: '8h', end: '18h' },
  mer: { open: true,  start: '8h', end: '18h' },
  jeu: { open: true,  start: '8h', end: '18h' },
  ven: { open: false, start: '8h', end: '18h' },
};

// ── scheduleToString ───────────────────────────────────────────────────────────
// Compresse les jours consécutifs identiques en ranges (ex: Lun-Ven: 8h-18h)
const scheduleToString = (s) => {
  const lines = [];
  let i = 0;
  while (i < DAYS_PARSE.length) {
    const { key } = DAYS_PARSE[i];
    const cur = s[key];
    let j = i + 1;
    while (
      j < DAYS_PARSE.length &&
      s[DAYS_PARSE[j].key].open  === cur.open &&
      s[DAYS_PARSE[j].key].start === cur.start &&
      s[DAYS_PARSE[j].key].end   === cur.end
    ) j++;
    const label = j - i > 1
      ? `${DAYS_PARSE[i].label}-${DAYS_PARSE[j - 1].label}`
      : DAYS_PARSE[i].label;
    lines.push(`${label}: ${cur.open ? `${cur.start}-${cur.end}` : 'Fermé'}`);
    i = j;
  }
  return lines.join('\n');
};

// ── parseScheduleString ────────────────────────────────────────────────────────
// Reconstruit un objet schedule depuis la chaîne Firestore.
// Gère aussi bien "Lun-Ven: 8h-18h" que "Sam: Fermé" ou "Dim: 9h-12h".
const parseScheduleString = (str) => {
  // Commence toujours par les defaults pour ne jamais avoir de clé manquante
  const s = Object.fromEntries(
    Object.entries(DEFAULT_SCHEDULE).map(([k, v]) => [k, { ...v }])
  );
  if (!str) return s;

  str.split('\n').forEach(line => {
    const m = line.match(/^(\w+(?:-\w+)?)\s*:\s*(.+)$/i);
    if (!m) return;

    const closed = /ferm/i.test(m[2]);
    const t = m[2].match(/(\d+h\d*)\s*-\s*(\d+h\d*)/);
    const [start, end] = t ? [t[1], t[2]] : ['8h', '18h'];

    // Normalise la casse du label pour la recherche (ex: "LUN" → "lun")
    const parts = m[1].split('-').map(x => x.trim().toLowerCase().slice(0, 3));
    const from  = DAYS_PARSE.findIndex(d => d.key === parts[0]);
    const toKey = parts[1] ? DAYS_PARSE.findIndex(d => d.key === parts[1]) : from;
    const to    = toKey === -1 ? from : toKey;

    if (from === -1) return; // label inconnu, on ignore

    for (let i = from; i <= to; i++) {
      s[DAYS_PARSE[i].key] = { open: !closed, start, end };
    }
  });

  return s;
};

// ── SchedulePicker ─────────────────────────────────────────────────────────────
const SchedulePicker = ({ schedule, onChange }) => {
  const toggle = (key) =>
    onChange({ ...schedule, [key]: { ...schedule[key], open: !schedule[key].open } });

  const setTime = (key, field, val) =>
    onChange({ ...schedule, [key]: { ...schedule[key], [field]: val } });

  const applyAll = (start, end) => {
    const n = {};
    DAYS_DISPLAY.forEach(({ key }) => { n[key] = { ...schedule[key], start, end }; });
    onChange(n);
  };

  return (
    <div>
      {/* Préréglages rapides */}
      <div className="schedule-presets">
        <span style={{
          fontSize: '0.75rem', fontWeight: 700,
          color: 'var(--md-text-secondary)', alignSelf: 'center',
        }}>
          Appliquer à tous :
        </span>
        {[
          ['8h–16h', '8h', '16h'],
          ['8h–17h', '8h', '17h'],
          ['8h–18h', '8h', '18h'],
          ['9h–20h', '9h', '20h'],
        ].map(([lbl, s, e]) => (
          <button key={lbl} type="button" className="preset-chip" onClick={() => applyAll(s, e)}>
            {lbl}
          </button>
        ))}
      </div>

      {/* Grille jours */}
      <div className="schedule-grid">
        {DAYS_DISPLAY.map(({ key, label }) => {
          const d = schedule[key];
          return (
            <div key={key} className={`schedule-row${d.open ? ' active' : ''}`}>
              <button
                type="button"
                className={`schedule-checkbox${d.open ? ' checked' : ''}`}
                onClick={() => toggle(key)}
                aria-label={`${d.open ? 'Désactiver' : 'Activer'} ${label}`}
              >
                {d.open && <Check size={13} color="#fff" strokeWidth={3} />}
              </button>
              <span className="schedule-day">{label}</span>
              {d.open ? (
                <>
                  <select
                    className="schedule-select"
                    value={d.start}
                    onChange={e => setTime(key, 'start', e.target.value)}
                  >
                    {SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <span className="schedule-sep">→</span>
                  <select
                    className="schedule-select"
                    value={d.end}
                    onChange={e => setTime(key, 'end', e.target.value)}
                  >
                    {SLOTS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </>
              ) : (
                <span className="schedule-closed-label">Fermé ce jour</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── ShopForm ───────────────────────────────────────────────────────────────────
const ShopForm = ({ shop, onSuccess }) => {
  const { user } = useAuth();
  const { getCurrentPosition } = useLocation();
  const [saving,     setSaving]     = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [locDone,    setLocDone]    = useState(false);

  const [form, setForm] = useState({
    name:      shop?.name      || '',
    address:   shop?.address   || '',
    phone:     shop?.phone     || '',
    whatsapp:  shop?.whatsapp  || '',
    latitude:  shop?.latitude  || '',
    longitude: shop?.longitude || '',
  });

  const [schedule,     setSchedule]     = useState(() => parseScheduleString(shop?.openHours));
  const [scheduleOnly, setScheduleOnly] = useState(shop?.managedBy === 'schedule');
  // Méta merchant (showPhone, showOwner, ownerName, commerceType) — lus depuis /merchants
  const [merchantMeta, setMerchantMeta] = useState({
    showPhone:    shop?.showPhone    ?? true,
    showOwner:    shop?.showOwner    ?? false,
    ownerName:    shop?.ownerName    || '',
    commerceType: shop?.commerceType || '',
  });
  // Vrai quand le useEffect a fini de lire /merchants (empêche un submit à ownerName vide)
  const [metaLoaded, setMetaLoaded] = useState(!!shop?.id);

  /* Pré-remplir depuis les infos d'inscription si nouveau magasin */
  useEffect(() => {
    if (shop?.managedBy) return;
    if (!user) return;
    getDoc(doc(db, 'merchants', user.uid)).then(snap => {
      const data = snap.data();
      if (!data) return;
      if (data.defaultManagedBy === 'schedule') setScheduleOnly(true);
      if (data.defaultShopName && !form.name)
        setForm(p => ({ ...p, name: data.defaultShopName }));
      if (data.phone && !form.phone)
        setForm(p => ({ ...p, phone: data.phone }));
      // Récupère les préférences d'affichage saisies à l'inscription
      // ownerName est maintenant un champ composite direct dans /merchants
      const ownerName = data.ownerName
        || [data.firstName, data.lastName].filter(Boolean).join(' ')
        || '';
      setMerchantMeta(p => ({
        ...p,
        showPhone:    data.showPhone    ?? p.showPhone,
        showOwner:    data.showOwner    ?? p.showOwner,
        ownerName:    ownerName         || p.ownerName,
        commerceType: data.commerceType || p.commerceType,
      }));
      setMetaLoaded(true);
    }).catch(() => { setMetaLoaded(true); }); // en cas d'erreur on débloque quand même
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, shop?.managedBy]);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  // GPS + reverse geocoding
  const detectAll = async () => {
    setLocLoading(true);
    setLocDone(false);
    try {
      const pos = await getCurrentPosition();
      setForm(p => ({ ...p, latitude: pos.lat, longitude: pos.lng }));

      const res  = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${pos.lat}&lon=${pos.lng}&format=json&accept-language=fr&zoom=18&addressdetails=1`,
        { headers: { 'Accept-Language': 'fr' } }
      );
      const data = await res.json();

      if (data?.address) {
        const a          = data.address;
        const streetNum  = a.house_number || '';
        const streetName = a.road || a.pedestrian || a.footway || a.path || '';
        const street     = streetNum && streetName ? `${streetNum} ${streetName}` : streetName;
        const quarter    = a.quarter || a.neighbourhood || a.suburb || a.hamlet || '';
        const city       = a.city || a.town || a.village || a.municipality || a.county || '';
        const postcode   = a.postcode || '';
        const parts      = [street, quarter, city, postcode].filter(Boolean);
        const address    = parts.length >= 2
          ? parts.join(', ')
          : data.display_name?.split(',').slice(0, 3).join(',').trim() || '';
        setForm(p => ({ ...p, address }));
      }
      setLocDone(true);
    } catch {
      alert('Impossible de détecter la position. Activez le GPS et réessayez.');
    } finally {
      setLocLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.address || !form.latitude) {
      alert('Veuillez remplir les champs obligatoires (*)');
      return;
    }
    setSaving(true);
    try {
      const data = {
        ...form,
        latitude:     parseFloat(form.latitude),
        longitude:    parseFloat(form.longitude),
        openHours:    scheduleToString(schedule),
        managedBy:    scheduleOnly ? 'schedule' : 'merchant',
        ownerId:      user.uid,
        // Champs issus des préférences merchant (inscription + édition)
        showPhone:    merchantMeta.showPhone,
        showOwner:    merchantMeta.showOwner,
        ownerName:    merchantMeta.ownerName,
        commerceType: merchantMeta.commerceType,
        status:       shop?.status || 'closed',
        updatedAt:    serverTimestamp(),
        createdAt:    shop?.createdAt || serverTimestamp(),
        // Si le magasin était rejeté et que le commerçant resoumet,
        // on repasse en "pending" pour notifier l'admin
        approval: shop?.approval === 'rejected' ? 'pending' : (shop?.approval || 'pending'),
        // On conserve la raison pour historique mais l'admin verra "En attente"
        rejectionReason: shop?.approval === 'rejected' ? '' : (shop?.rejectionReason || ''),
      };
      let shopId;
      if (shop?.id) {
        await updateDoc(doc(db, 'shops', shop.id), data);
        shopId = shop.id;
      } else {
        const ref = doc(collection(db, 'shops'));
        await setDoc(ref, data);
        shopId = ref.id;
        await updateDoc(doc(db, 'merchants', user.uid), { shopId, hasShop: true });
      }
      onSuccess?.({ id: shopId, ...data });
    } catch (err) {
      console.error(err);
      alert('Erreur lors de la sauvegarde : ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>

      {/* Nom */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label"><Store size={13} /> Nom du magasin *</label>
        <div className="form-input-wrap">
          <Store size={16} className="form-input-icon" />
          <input
            className="form-input" type="text" value={form.name} onChange={set('name')}
            placeholder="Nom du magasin" required autoComplete="organization"
          />
        </div>
      </div>

      {/* Bouton GPS */}
      <button
        type="button"
        onClick={detectAll}
        disabled={locLoading}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.65rem',
          padding: '1rem', borderRadius: 'var(--md-radius-lg)', border: 'none',
          background: locDone ? 'var(--md-primary-light)' : 'var(--md-primary)',
          color: locDone ? 'var(--md-primary-dark)' : '#fff',
          fontWeight: 700, fontSize: '1rem',
          cursor: locLoading ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s', fontFamily: 'inherit',
          outline: locDone ? '2.5px solid var(--md-primary)' : 'none',
          boxShadow: locDone ? 'none' : '0 4px 14px rgba(0,201,167,.35)',
        }}
      >
        {locLoading
          ? <Loader size={20} style={{ animation: 'spin 0.8s linear infinite' }} />
          : locDone ? <Check size={20} /> : <MapPin size={20} />
        }
        {locLoading
          ? 'Détection en cours…'
          : locDone
            ? '✓ Position & adresse détectées'
            : 'Détecter ma position & adresse'
        }
      </button>
      <p style={{
        margin: '-0.75rem 0 0', fontSize: '0.75rem',
        color: 'var(--md-text-secondary)', textAlign: 'center',
      }}>
        Un seul appui remplit automatiquement l'adresse et les coordonnées GPS
      </p>

      {/* Adresse */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label"><MapPin size={13} /> Adresse *</label>
        <div className="form-input-wrap">
          <MapPin size={16} className="form-input-icon" />
          <input
            className="form-input" type="text" value={form.address} onChange={set('address')}
            placeholder="Détectée auto ou saisie manuelle" required
          />
        </div>
      </div>

      {/* GPS coords — lecture seule, remplis uniquement via le bouton GPS */}
      {form.latitude && form.longitude ? (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.6rem 0.9rem',
          borderRadius: 'var(--md-radius-md)',
          background: 'rgba(0,201,167,0.08)',
          border: '1px solid rgba(0,201,167,0.25)',
          fontSize: '0.78rem', color: 'var(--md-text-secondary)',
        }}>
          <MapPin size={14} style={{ color: 'var(--md-primary)', flexShrink: 0 }} />
          <span>
            Position enregistrée&nbsp;:&nbsp;
            <strong style={{ color: 'var(--md-text-primary)', fontVariantNumeric: 'tabular-nums' }}>
              {parseFloat(form.latitude).toFixed(5)}, {parseFloat(form.longitude).toFixed(5)}
            </strong>
          </span>
          {/* Champs cachés pour la soumission du formulaire */}
          <input type="hidden" name="latitude"  value={form.latitude} />
          <input type="hidden" name="longitude" value={form.longitude} />
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.6rem 0.9rem',
          borderRadius: 'var(--md-radius-md)',
          background: 'rgba(245,158,11,0.08)',
          border: '1px solid rgba(245,158,11,0.25)',
          fontSize: '0.78rem', color: 'var(--md-text-secondary)',
        }}>
          <MapPin size={14} style={{ color: '#f59e0b', flexShrink: 0 }} />
          <span>Appuyez sur <strong>Détecter ma position</strong> pour placer votre magasin sur la carte.</span>
        </div>
      )}

      {/* Téléphone */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label"><Phone size={13} /> Téléphone</label>
        <div className="form-input-wrap">
          <Phone size={16} className="form-input-icon" />
          <input
            className="form-input" type="tel" value={form.phone} onChange={set('phone')}
            placeholder="01 23 45 67 89" autoComplete="tel"
          />
        </div>
      </div>

      {/* WhatsApp */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label className="form-label">
          <MessageCircle size={13} /> WhatsApp
          <span style={{ fontSize: '0.68rem', fontWeight: 500, color: 'var(--text-3)', marginLeft: '0.4rem' }}>
            (Pack Premium / Pro)
          </span>
        </label>
        <div className="form-input-wrap">
          <MessageCircle size={16} className="form-input-icon" />
          <input
            className="form-input" type="tel" value={form.whatsapp} onChange={set('whatsapp')}
            placeholder="0555 12 34 56" autoComplete="tel"
          />
        </div>
        <p style={{ fontSize: '0.68rem', color: 'var(--text-3)', margin: '0.3rem 0 0', lineHeight: 1.4 }}>
          Affiché dans l'onglet boutique pour les clients (Pack Premium+).
        </p>
      </div>

      {/* ── Visibilité : showPhone + showOwner ── */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.55rem' }}>
        <p style={{ fontSize: '.68rem', fontWeight: 800, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.7px', margin: 0 }}>
          Informations visibles par les clients
        </p>

        {/* Toggle showPhone — visible uniquement si un numéro est saisi */}
        {form.phone && (
          <div
            onClick={() => setMerchantMeta(p => ({ ...p, showPhone: !p.showPhone }))}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              gap: '.75rem', padding: '.75rem 1rem', borderRadius: 'var(--r-lg)',
              background: 'var(--bg)', cursor: 'pointer',
              boxShadow: merchantMeta.showPhone ? 'var(--neu-in-sm)' : 'var(--neu-sm)',
              transition: 'box-shadow .2s',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
              <Phone size={15} color={merchantMeta.showPhone ? 'var(--primary)' : 'var(--text-3)'} strokeWidth={2} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text)' }}>Afficher mon numéro</div>
                <div style={{ fontSize: '.72rem', color: 'var(--text-2)', marginTop: '.05rem' }}>
                  Les clients pourront vous appeler depuis la fiche
                </div>
              </div>
            </div>
            <label className="switch" onClick={e => e.stopPropagation()}>
              <input type="checkbox" checked={merchantMeta.showPhone}
                onChange={() => setMerchantMeta(p => ({ ...p, showPhone: !p.showPhone }))}
                aria-label="Afficher le numéro" />
              <span className="switch-track" />
            </label>
          </div>
        )}

        {/* Toggle showOwner */}
        <div
          onClick={() => setMerchantMeta(p => ({ ...p, showOwner: !p.showOwner }))}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '.75rem', padding: '.75rem 1rem', borderRadius: 'var(--r-lg)',
            background: 'var(--bg)', cursor: 'pointer',
            boxShadow: merchantMeta.showOwner ? 'var(--neu-in-sm)' : 'var(--neu-sm)',
            transition: 'box-shadow .2s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '.6rem' }}>
            <User size={15} color={merchantMeta.showOwner ? 'var(--primary)' : 'var(--text-3)'} strokeWidth={2} />
            <div>
              <div style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--text)' }}>Afficher mon nom</div>
              <div style={{ fontSize: '.72rem', color: 'var(--text-2)', marginTop: '.05rem' }}>
                {merchantMeta.ownerName
                  ? `"${merchantMeta.ownerName}" apparaîtra sur la fiche`
                  : 'Votre nom apparaîtra sur la fiche du magasin'}
              </div>
            </div>
          </div>
          <label className="switch" onClick={e => e.stopPropagation()}>
            <input type="checkbox" checked={merchantMeta.showOwner}
              onChange={() => setMerchantMeta(p => ({ ...p, showOwner: !p.showOwner }))}
              aria-label="Afficher le nom" />
            <span className="switch-track" />
          </label>
        </div>
      </div>

      {/* ── Type de commerce ── */}
      <div>
        <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '.4rem', marginBottom: '.65rem' }}>
          <Tag size={13} /> Type de commerce *
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '.45rem' }}>
          {COMMERCE_TYPES.map(t => {
            const Icon = t.icon;
            const sel  = merchantMeta.commerceType === t.key;
            return (
              <button key={t.key} type="button"
                onClick={() => setMerchantMeta(p => ({ ...p, commerceType: t.key }))}
                aria-pressed={sel}
                style={{
                  padding: '.65rem .3rem', borderRadius: 'var(--r-lg)', border: 'none',
                  cursor: 'pointer', fontFamily: 'inherit',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '.28rem',
                  background: 'var(--bg)',
                  boxShadow: sel ? 'var(--neu-in-sm)' : 'var(--neu-sm)',
                  transition: 'box-shadow .18s',
                }}>
                <Icon size={17} strokeWidth={1.75} color={sel ? 'var(--primary)' : 'var(--text-2)'} />
                <span style={{ fontSize: '.6rem', fontWeight: 700, color: sel ? 'var(--primary)' : 'var(--text-2)', textAlign: 'center', lineHeight: 1.25 }}>
                  {t.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Horaires */}
      <div>
        <label className="form-label" style={{
          marginBottom: '0.6rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
        }}>
          <Clock size={13} /> Horaires d'ouverture *
        </label>
        <SchedulePicker schedule={schedule} onChange={setSchedule} />
      </div>

      {/* Mode gestion — sans smartphone */}
      <div
        onClick={() => setScheduleOnly(v => !v)}
        style={{
          display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
          padding: '1rem', borderRadius: 'var(--md-radius-lg)',
          border: `2px solid ${scheduleOnly ? 'var(--md-primary)' : 'var(--md-outline)'}`,
          background: scheduleOnly ? 'var(--md-primary-light)' : 'var(--md-surface-2)',
          cursor: 'pointer', transition: 'all 0.2s',
        }}
      >
        <div style={{
          width: 20, height: 20, borderRadius: 4, flexShrink: 0, marginTop: 2,
          border: `2px solid ${scheduleOnly ? 'var(--md-primary)' : 'var(--md-text-secondary)'}`,
          background: scheduleOnly ? 'var(--md-primary)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
        }}>
          {scheduleOnly && <Check size={13} color="#fff" strokeWidth={3} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 700, fontSize: '0.88rem',
            color: 'var(--md-text-primary)', marginBottom: '0.2rem',
            display: 'flex', alignItems: 'center', gap: '0.4rem',
          }}>
            Magasin sans smartphone
            <Tooltip
              text="En cochant cette case, le statut de votre magasin sera géré automatiquement selon vos horaires : ouvert le matin à l'heure indiquée, fermé le soir. Aucune action de votre part n'est nécessaire. ⚠️ En contrepartie, vos clients verront un badge indiquant que le statut n'est pas confirmé en temps réel."
              position="right"
              icon
            />
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--md-text-secondary)', lineHeight: 1.5 }}>
            Le statut sera géré automatiquement selon les horaires d'ouverture.
            Aucune confirmation manuelle requise — mais le statut peut ne pas
            refléter la réalité en temps réel.
          </div>
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        className="btn btn-primary btn-large"
        disabled={saving || !metaLoaded}
        style={{ marginTop: '0.5rem' }}
      >
        {saving         ? 'Enregistrement…'
         : !metaLoaded  ? 'Chargement…'
         : shop         ? 'Mettre à jour'
         :                'Créer mon magasin'}
      </button>
    </form>
  );
};

export default ShopForm;